import { useEffect, useState, useCallback } from "react";
import { useAuth, useUser } from "@clerk/react";
import { format } from "date-fns";
import { Check, Loader2, MessageSquareWarning, Pencil, Trash2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export interface UnresolvedReply {
  authorEmail: string;
  authorUserId?: string;
  comment: string;
  createdAt: string;
  editedAt?: string;
}

export interface UnresolvedAnnotation {
  field: string;
  fieldLabel?: string;
  comment: string;
  approverEmail: string;
  decidedAt: string | null;
  stage: string;
  stepId?: number;
  annotationIndex?: number;
  replies?: UnresolvedReply[];
}

const STAGE_LABEL: Record<string, string> = {
  finance_review: "Finance Review",
  hr_review: "HR Review",
  sport_admin_review: "Sport Admin Review",
  legal_review: "Legal Review",
  compliance_review: "Compliance Review",
  ad_review: "AD Review",
  signature: "Signature",
};

export function useUnresolvedApproverNotes(contractId: number | undefined) {
  const { getToken } = useAuth();
  const [notes, setNotes] = useState<UnresolvedAnnotation[]>([]);
  const [byField, setByField] = useState<Record<string, UnresolvedAnnotation[]>>({});
  const [approvalId, setApprovalId] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!contractId) return;
    try {
      const token = await getToken();
      const res = await fetch(`/api/contracts/${contractId}/approval`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = (await res.json()) as {
        approval?: { id: number } | null;
        unresolvedAnnotations?: UnresolvedAnnotation[];
        unresolvedAnnotationsByField?: Record<string, UnresolvedAnnotation[]>;
      };
      const flat = data.unresolvedAnnotations ?? [];
      setNotes(flat);
      setApprovalId(data.approval?.id ?? null);
      // Prefer the grouped payload from the server; fall back to client-side
      // grouping if older server hasn't deployed yet.
      const grouped =
        data.unresolvedAnnotationsByField ??
        flat.reduce<Record<string, UnresolvedAnnotation[]>>((acc, n) => {
          (acc[n.field] ??= []).push(n);
          return acc;
        }, {});
      setByField(grouped);
    } catch {
      /* ignore */
    }
  }, [contractId, getToken]);

  useEffect(() => { void load(); }, [load]);

  return { notes, byField, approvalId, refresh: load };
}

interface BadgeProps {
  notes: UnresolvedAnnotation[] | undefined;
  field: string;
  className?: string;
  approvalId?: number | null;
  onResolved?: () => void;
}

export function ApproverNoteBadge({ notes, field, className, approvalId, onResolved }: BadgeProps) {
  const { userId, getToken } = useAuth();
  const { user } = useUser();
  const currentUserEmail = user?.primaryEmailAddress?.emailAddress?.toLowerCase() ?? null;
  const [resolvingKey, setResolvingKey] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<{ key: string; message: string } | null>(null);

  if (!notes || notes.length === 0) return null;

  const replyKeyOf = (n: UnresolvedAnnotation, ri: number): string =>
    `${n.stepId ?? "?"}:${n.field}:${n.annotationIndex ?? 0}:r${ri}`;

  const isMyReply = (r: UnresolvedReply): boolean => {
    if (r.authorUserId) {
      return !!userId && r.authorUserId === userId;
    }
    // Legacy replies (pre-authorUserId) — fall back to case-insensitive email
    // match so the original drafter can still edit/delete their historical
    // replies. Mirrors the backend's loadReplyTargetStep ownership check.
    if (!currentUserEmail || typeof r.authorEmail !== "string") return false;
    return r.authorEmail.toLowerCase() === currentUserEmail;
  };

  const handleSaveEdit = async (n: UnresolvedAnnotation, ri: number, key: string) => {
    if (!approvalId || typeof n.stepId !== "number") return;
    const trimmed = editDraft.trim();
    if (!trimmed) return;
    setSavingKey(key);
    setErrorKey(null);
    try {
      const token = await getToken();
      const res = await fetch(`/api/approvals/${approvalId}/annotations/reply`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ stepId: n.stepId, field: n.field, replyIndex: ri, comment: trimmed }),
      });
      if (res.ok) {
        setEditingKey(null);
        setEditDraft("");
        onResolved?.();
        return;
      }
      let message = `Couldn't save edit (${res.status}).`;
      try {
        const body = (await res.json()) as { message?: string; error?: string };
        if (body.message) message = body.message;
        else if (body.error) message = body.error;
      } catch { /* ignore */ }
      setErrorKey({ key, message });
    } catch {
      setErrorKey({ key, message: "Couldn't save edit. Check your connection and try again." });
    } finally {
      setSavingKey(null);
    }
  };

  const handleDelete = async (n: UnresolvedAnnotation, ri: number, key: string) => {
    if (!approvalId || typeof n.stepId !== "number") return;
    setDeletingKey(key);
    setErrorKey(null);
    try {
      const token = await getToken();
      const res = await fetch(`/api/approvals/${approvalId}/annotations/reply`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ stepId: n.stepId, field: n.field, replyIndex: ri }),
      });
      if (res.ok) {
        onResolved?.();
        return;
      }
      let message = `Couldn't delete reply (${res.status}).`;
      try {
        const body = (await res.json()) as { message?: string; error?: string };
        if (body.message) message = body.message;
        else if (body.error) message = body.error;
      } catch { /* ignore */ }
      setErrorKey({ key, message });
    } catch {
      setErrorKey({ key, message: "Couldn't delete reply. Check your connection and try again." });
    } finally {
      setDeletingKey(null);
    }
  };

  const handleResolve = async (n: UnresolvedAnnotation) => {
    if (!approvalId || typeof n.stepId !== "number") return;
    const key = `${n.stepId}:${n.field}:${n.annotationIndex ?? 0}`;
    setResolvingKey(key);
    try {
      const token = await getToken();
      const res = await fetch(`/api/approvals/${approvalId}/annotations/resolve`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ stepId: n.stepId, field: n.field }),
      });
      if (res.ok) onResolved?.();
    } catch {
      /* ignore */
    } finally {
      setResolvingKey(null);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${notes.length} unresolved approver note${notes.length === 1 ? "" : "s"}`}
          title={`${notes.length} unresolved approver note${notes.length === 1 ? "" : "s"}`}
          data-testid={`approver-note-badge-${field}`}
          className={
            "inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 hover:bg-amber-100 hover:border-amber-400 transition-colors " +
            (className ?? "")
          }
        >
          <MessageSquareWarning className="h-3 w-3" />
          {notes.length}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 p-0"
        data-testid={`approver-note-popover-${field}`}
      >
        <div className="px-3 py-2 border-b bg-amber-50/60">
          <div className="text-xs font-semibold text-amber-900">
            Approver note{notes.length === 1 ? "" : "s"} on this field
          </div>
          <div className="text-[10px] text-amber-800/80 mt-0.5">
            From the latest review that returned this contract for changes.
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto divide-y">
          {notes.map((n, i) => {
            const key = `${n.stepId ?? "?"}:${n.field}:${n.annotationIndex ?? i}`;
            const canResolve = !!approvalId && typeof n.stepId === "number";
            const isResolving = resolvingKey === key;
            return (
              <div key={i} className="px-3 py-2 text-xs space-y-1">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-medium text-foreground">{n.approverEmail}</span>
                  <span className="text-[10px] text-muted-foreground">
                    {STAGE_LABEL[n.stage] ?? n.stage}
                    {n.decidedAt && <> · {format(new Date(n.decidedAt), "MMM d, h:mm a")}</>}
                  </span>
                </div>
                <div className="whitespace-pre-wrap text-foreground/90">{n.comment}</div>
                {n.replies && n.replies.length > 0 && (
                  <div
                    className="mt-1 space-y-1 pl-2 border-l-2 border-amber-300/60"
                    data-testid={`approver-note-replies-${field}-${i}`}
                  >
                    {n.replies.map((r, ri) => {
                      const rKey = replyKeyOf(n, ri);
                      const mine = isMyReply(r);
                      const isEditing = editingKey === rKey;
                      const isSaving = savingKey === rKey;
                      const isDeleting = deletingKey === rKey;
                      const showError = errorKey?.key === rKey;
                      return (
                        <div key={ri} className="text-[11px] text-foreground/85">
                          <div className="flex items-center gap-1 flex-wrap">
                            <span className="font-medium">{r.authorEmail}</span>
                            <span className="font-normal text-muted-foreground">
                              · {format(new Date(r.createdAt), "MMM d, h:mm a")}
                              {r.editedAt && <> · edited</>}
                            </span>
                            {mine && approvalId && !isEditing && (
                              <span className="ml-auto inline-flex items-center gap-0.5">
                                <button
                                  type="button"
                                  className="p-0.5 text-amber-800 hover:text-amber-950"
                                  aria-label="Edit reply"
                                  data-testid={`approver-note-reply-edit-${field}-${i}-${ri}`}
                                  onClick={() => {
                                    setEditingKey(rKey);
                                    setEditDraft(r.comment);
                                    setErrorKey(null);
                                  }}
                                  disabled={isSaving || isDeleting}
                                >
                                  <Pencil className="h-3 w-3" />
                                </button>
                                <button
                                  type="button"
                                  className="p-0.5 text-amber-800 hover:text-red-700"
                                  aria-label="Delete reply"
                                  data-testid={`approver-note-reply-delete-${field}-${i}-${ri}`}
                                  onClick={() => void handleDelete(n, ri, rKey)}
                                  disabled={isSaving || isDeleting}
                                >
                                  {isDeleting ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-3 w-3" />
                                  )}
                                </button>
                              </span>
                            )}
                          </div>
                          {isEditing ? (
                            <div
                              className="mt-1 space-y-1"
                              data-testid={`approver-note-reply-editor-${field}-${i}-${ri}`}
                            >
                              <Textarea
                                autoFocus
                                value={editDraft}
                                onChange={(e) => setEditDraft(e.target.value)}
                                className="min-h-[48px] text-[11px] bg-white"
                              />
                              {showError && (
                                <div className="text-[10px] text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">
                                  {errorKey?.message}
                                </div>
                              )}
                              <div className="flex gap-1.5">
                                <Button
                                  size="sm"
                                  className="h-6 px-2 text-[10px]"
                                  onClick={() => void handleSaveEdit(n, ri, rKey)}
                                  disabled={!editDraft.trim() || isSaving}
                                >
                                  {isSaving ? "Saving…" : "Save"}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 px-2 text-[10px]"
                                  onClick={() => {
                                    setEditingKey(null);
                                    setEditDraft("");
                                    setErrorKey(null);
                                  }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="whitespace-pre-wrap">{r.comment}</div>
                              {showError && (
                                <div className="mt-0.5 text-[10px] text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">
                                  {errorKey?.message}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {canResolve && (
                  <div className="flex justify-end pt-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-[10px] text-amber-900 hover:text-amber-950 hover:bg-amber-100"
                      onClick={() => void handleResolve(n)}
                      disabled={isResolving}
                      data-testid={`approver-note-resolve-${field}-${i}`}
                    >
                      {isResolving ? (
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      ) : (
                        <Check className="h-3 w-3 mr-1" />
                      )}
                      {isResolving ? "Resolving…" : "Mark resolved"}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
